import React, { useState } from 'react';
import { getBuildPaymentPlan } from '../utils/buildPaymentPlan';
interface PaymentScheduleProps {
  buildFee: number;
  monthlyService: number;
  adSpend: number;
  hiresLow: number;
  hiresHigh: number;
  splitBuildFee: boolean;
  onSplitBuildFeeChange: (split: boolean) => void;
  showPayments: boolean;
  onShowPaymentsChange: (show: boolean) => void;
  hasLeader: boolean;
  leaderBuildFee: number;
  leaderMonthlyFee: number;
  leaderActiveFee: number;
  leaderAdSpendLow: number;
  leaderAdSpendHigh: number;
}
const usd = (value: number) => '$' + value.toLocaleString('en-US');
export function PaymentSchedule({
  buildFee,
  monthlyService,
  adSpend,
  hiresLow,
  hiresHigh,
  splitBuildFee,
  onSplitBuildFeeChange,
  showPayments,
  onShowPaymentsChange,
  hasLeader,
  leaderBuildFee,
  leaderMonthlyFee,
  leaderActiveFee,
  leaderAdSpendLow,
  leaderAdSpendHigh
}: PaymentScheduleProps) {
  // 0 = hidden, 1 = HIRE total, 2 = plus ad spend, 3 = plus the full breakdown.
  const [stage, setStage] = useState(showPayments ? 1 : 0);
  const detail = stage - 1;
  const plan = getBuildPaymentPlan(buildFee);
  // When split, payment 1 lands at signing and payments 2 and 3 land in
  // months 2 and 3. Month 1 carries no build payment.
  const buildAtSigning = splitBuildFee ? plan.down : buildFee;
  const buildInstallment = splitBuildFee ? plan.monthly : 0;
  // The leader retention bonus is performance based, so it rides in the
  // "+ bonuses" language rather than the monthly service lines.
  const leaderFee = 0;
  // With a leadership campaign, only the implementation and the first month of
  // the leadership campaign are collected up front.
  const dueAtSigning = hasLeader ? buildAtSigning + leaderBuildFee + leaderMonthlyFee : buildAtSigning + monthlyService;
  const dailyAdSpend = Math.round(adSpend / 30);
  const monthlyWithLeader = monthlyService + leaderFee;
  const ongoingMonthly = monthlyWithLeader + adSpend;
  const leaderLine: {
    label: string;
    value: string;
  }[] = [];
  const leaderPhase = {
    window: '2–8 weeks',
    title: 'Leadership Campaign',
    feeLabel: 'HIRE fees per month',
    feeAmount: usd(leaderMonthlyFee),
    feeUnit: '/ mo',
    plusBonuses: false,
    showSplitToggle: false,
    feeLines: [{
      label: 'Month 1',
      value: 'Already paid'
    }, {
      label: 'Month 2 onward',
      value: usd(leaderMonthlyFee) + ' / mo'
    }],
    feeNote: 'Same monthly service structure as a standard campaign, priced at the ' + usd(leaderAdSpendLow) + ' of ad spend a leadership campaign runs on.',
    adAmount: usd(leaderAdSpendLow),
    adUnit: '/ mo',
    adDaily: '(' + usd(Math.round(leaderAdSpendLow / 30)) + ' / day)',
    allInAmount: usd(leaderMonthlyFee + leaderAdSpendLow),
    allInUnit: '/ mo',
    expect: ['Targeted outreach to experienced operators', 'Interviews run and vetted by HIRE', 'First month of service already paid at signing', 'Standard campaign launches once the leader signs'],
    primary: false
  };
  const phases = [{
    window: 'First 9–30 days',
    title: 'Implementation and Alignment',
    feeLabel: 'HIRE fees due at signing',
    feeAmount: usd(dueAtSigning),
    feeUnit: 'one time',
    plusBonuses: false,
    showSplitToggle: true,
    feeLines: [{
      label: splitBuildFee ? 'Implementation 1/3' : 'Implementation',
      value: usd(buildAtSigning + (hasLeader ? leaderBuildFee : 0))
    }, ...(hasLeader ? [{
      label: 'First month, leader',
      value: usd(leaderMonthlyFee)
    }] : [{
      label: 'First month of service',
      value: usd(monthlyService)
    }])],
    feeNote: splitBuildFee ? 'Payments 2 and 3 of ' + usd(plan.monthly) + ' land in months 2 and 3, so month 1 carries no implementation payment.' : 'Nothing goes to the job platforms until your campaign is live.',
    adAmount: usd(0),
    adUnit: 'not running yet',
    adDaily: '',
    allInAmount: usd(dueAtSigning),
    allInUnit: 'one time',
    expect: ['System, funnel, and creative built', 'Full alignment with your team', '3–4 hours of training and alignment calls', 'No ad spend running yet'],
    primary: true
  }, {
    window: 'Month 1',
    title: 'Launch and Refinement',
    feeLabel: 'HIRE fees this month',
    feeAmount: hasLeader ? usd(monthlyService) : usd(0),
    feeUnit: hasLeader ? '/ mo' : 'already paid',
    plusBonuses: false,
    showSplitToggle: false,
    feeLines: [{
      label: 'Monthly service',
      value: hasLeader ? usd(monthlyService) : 'Already paid'
    }, {
      label: 'Implementation',
      value: splitBuildFee ? 'No payment due' : 'Already paid'
    }],
    feeNote: hasLeader ? 'The standard campaign starts billing when it launches. The leader fee starts at day 30 active.' : 'Your first month of service was covered at signing.',
    adAmount: usd(adSpend),
    adUnit: '/ mo',
    adDaily: '(' + usd(dailyAdSpend) + ' / day)',
    allInAmount: usd(adSpend + (hasLeader ? monthlyService : 0)),
    allInUnit: '/ mo',
    expect: ['Your first ' + hiresLow + '–' + hiresHigh + ' hires', 'Weekly support and consulting', 'Baseline KPIs established', 'Retention best practices in place'],
    primary: false
  }, {
    window: 'Months 2–3',
    title: 'Optimization and Scale',
    feeLabel: 'HIRE fees per month',
    feeAmount: usd(monthlyWithLeader + buildInstallment),
    feeUnit: '/ mo',
    plusBonuses: true,
    showSplitToggle: false,
    feeLines: [{
      label: 'Monthly service',
      value: usd(monthlyService)
    }, ...leaderLine, ...(splitBuildFee ? [{
      label: 'Implementation 2+3',
      value: usd(buildInstallment)
    }] : [])],
    feeNote: splitBuildFee ? 'Your final two implementation payments land here, one in each month. Bonuses are ' + usd(500) + ' at day 15, then ' + usd(1000) + ' each at day 45 and day 90.' : 'Bonuses are ' + usd(500) + ' at day 15, then ' + usd(1000) + ' each at day 45 and day 90, only for hires still active in the field.',
    adAmount: usd(adSpend),
    adUnit: '/ mo',
    adDaily: '(' + usd(dailyAdSpend) + ' / day)',
    allInAmount: usd(ongoingMonthly + buildInstallment),
    allInUnit: '/ mo',
    expect: ['Conversion rates climb', 'Retention improves', 'ROI shows up', 'Most partners increase investment here'],
    primary: false
  }, {
    window: 'Month 4 onward',
    title: 'Predictable Engine',
    feeLabel: 'HIRE fees per month',
    feeAmount: usd(monthlyWithLeader),
    feeUnit: '/ mo',
    plusBonuses: true,
    showSplitToggle: false,
    feeLines: [{
      label: 'Monthly service',
      value: usd(monthlyService)
    }, ...leaderLine],
    feeNote: hasLeader ? 'Bonuses vary with your retention results. The leader fee is capped at 6 payments.' : 'Bonuses vary with your retention results.',
    adAmount: usd(adSpend),
    adUnit: '/ mo',
    adDaily: '(' + usd(dailyAdSpend) + ' / day)',
    allInAmount: usd(ongoingMonthly),
    allInUnit: '/ mo',
    expect: ['Forecastable hiring output', 'Scope moves with your ad spend', 'Continuous optimization'],
    primary: true
  }];
  if (hasLeader) {
    // Sourcing the leader happens between implementation and the standard launch.
    phases.splice(1, 0, leaderPhase);
  }
  return <div className="relative" data-id="element-244" data-mp="148xq3hssxm51">
      <div className="flex justify-end mb-3" data-id="element-245" data-mp="1497e4zuadj83">
        <button type="button" onClick={() => {
        const next = (stage + 1) % 4;
        setStage(next);
        onShowPaymentsChange(next > 0);
      }} className="text-[10px] font-bold uppercase tracking-widest text-slate-700 hover:text-[#7FE0C3] transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3] rounded" data-id="element-246" data-mp="qj4211p4f5wd">
          {stage === 0 ? 'Payments' : stage === 1 ? 'All-in' : stage === 2 ? 'Breakdown' : 'Hide'}
        </button>
      </div>

      {/* Connector line across the phases (desktop only) */}
      <div className="hidden lg:block absolute top-[2.1rem] h-px bg-[#7FE0C3]/20" style={hasLeader ? {
      left: '10%',
      right: '10%'
    } : {
      left: '12.5%',
      right: '12.5%'
    }} data-id="element-247" data-mp="25zung7ogwcy3" />

      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 relative ${hasLeader ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`} data-id="element-248" data-mp="3mhcwl4t9nu2g">
        {phases.map(phase => <div key={phase.title} className="flex flex-col" data-id="element-249" data-mp="3vg86orzflob1">
            {/* Node */}
            <div className="hidden lg:flex justify-center mb-5" data-id="element-250" data-mp="dzgqu85lwthq">
              <div className={`w-6 h-6 rounded-full bg-[#0A0F1C] border-2 flex items-center justify-center ${phase.primary ? 'border-[#7FE0C3]' : 'border-slate-700'}`} data-id="element-251" data-mp="e1t78gvpuvso">
                <div className={`w-2 h-2 rounded-full ${phase.primary ? 'bg-[#7FE0C3]' : 'bg-slate-600'}`} data-id="element-252" data-mp="myajjnftgzq0" />
              </div>
            </div>

            <div className={`flex-1 flex flex-col rounded-xl p-5 bg-slate-900/40 border ${phase.primary ? 'border-[#7FE0C3]/40' : 'border-slate-800'}`} data-id="element-253" data-mp="2os9u26l47d1n">
              <div className="lg:min-h-[5.25rem]" data-id="element-254" data-mp="3fnldjzh5fbll">
                <div className="text-2xl font-bold text-[#7FE0C3] leading-tight" data-id="element-255" data-mp="3anz0pr58fxdb">
                  {phase.window}
                </div>
                <div className="text-base font-medium text-white mt-1 leading-snug" data-id="element-256" data-mp="36n5fx6n36yon">
                  {phase.title}
                </div>
              </div>

              {/* What to expect */}
              <div className="mt-4 pt-4 border-t border-slate-800" data-id="element-257" data-mp="26uhlft6zk6q">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2.5" data-id="element-258" data-mp="3ubydt8n9rglw">
                  What to expect
                </div>
                <ul className="space-y-2 lg:min-h-[9.5rem]" data-id="element-259" data-mp="3pj58aijz848r">
                  {phase.expect.map(item => <li key={item} className="flex items-start" data-id="element-260" data-mp="3kjsknvla6t54">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#7FE0C3]/60 mr-2.5 mt-1.5 shrink-0" data-id="element-261" data-mp="lwof8ail8tiv" />
                      <span className="text-slate-300 text-sm leading-snug" data-id="element-262" data-mp="h4ejueypf197">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>

              {/* Economics, revealed by the Payments control */}
              {showPayments && <div className="mt-auto pt-4" data-id="element-263" data-mp="ygle2kzod101">
                  {/* HIRE fees, with the breakdown folded into the same panel */}
                  <dl className="pt-4 border-t border-slate-800 space-y-2 text-sm" data-id="element-264" data-mp="17fgipsn2m318">
                    <div className="flex justify-between gap-3" data-id="element-265" data-mp="17dcetap6940y">
                      <dt className="text-slate-500 self-end" data-id="element-266" data-mp="1ccp4s4bcar7r">HIRE</dt>
                      <dd className="text-2xl font-bold text-white text-right leading-tight tracking-tight whitespace-nowrap" data-id="element-267" data-mp="1h4qf3koy67v5">
                        {phase.feeAmount}{' '}
                        {phase.showSplitToggle ? <button type="button" onClick={() => onSplitBuildFeeChange(!splitBuildFee)} aria-pressed={splitBuildFee} aria-label={splitBuildFee ? 'Show the build fee as a single payment' : 'Adjust the build fee payment terms'} className="text-xs font-medium text-slate-500 hover:text-[#7FE0C3] transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3] rounded" data-id="element-268" data-mp="1q79efutiy2cn">
                            {splitBuildFee ? 'split' : phase.feeUnit}
                          </button> : <span className="text-xs font-medium text-slate-500" data-id="element-269" data-mp="irkt2zvojxtk">
                            {phase.feeUnit}
                          </span>}
                        {phase.plusBonuses && <span className="text-xs font-medium text-[#7FE0C3]" data-id="element-270" data-mp="2kkr3v20fslwk">
                            {' '}
                            + bonuses
                          </span>}
                      </dd>
                    </div>

                    {detail === 2 && <div className="pt-2 mt-1 border-t border-slate-800/70 space-y-1.5 text-[13px]" data-id="element-271" data-mp="r8bp6cov4w90">
                        {/* Always three rows so the columns stay level. */}
                        {[0, 1, 2].map(index => {
                  const line = phase.feeLines[index];
                  return <div key={line ? line.label : `spacer-${index}`} className="flex justify-between gap-3" data-id="element-272" data-mp="m7nnmx30cx0j">
                              <span className="text-slate-500 truncate" data-id="element-273" data-mp="cdnk2zndjztx">
                                {line ? line.label : '\u00A0'}
                              </span>
                              <span className="text-slate-400 text-right whitespace-nowrap" data-id="element-274" data-mp="26p2dbqflghef">
                                {line ? line.value : '\u00A0'}
                              </span>
                            </div>;
                })}
                      </div>}
                  </dl>

                  {detail > 0 && <div className="mt-3 rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2.5 flex justify-between gap-3 text-[13px]" data-id="element-275" data-mp="131f4c097szky">
                      <span className="text-slate-500 self-end" data-id="element-276" data-mp="u9mzc52yminr">Ads</span>
                      <span className="text-lg font-bold text-slate-200 text-right whitespace-nowrap leading-tight" data-id="element-277" data-mp="u7jx8moj8z2o">
                        {phase.adAmount}{' '}
                        <span className="text-slate-500 text-xs font-normal" data-id="element-278" data-mp="kci8vt230115">
                          {phase.adUnit}
                        </span>
                      </span>
                    </div>}

                  {detail === 2 && <div className="mt-2 rounded-lg border border-[#7FE0C3]/40 bg-[#7FE0C3]/5 px-3 py-2.5 flex justify-between gap-3 text-[13px]" data-id="element-279" data-mp="2mye6hoh1frgt">
                      <span className="text-[#7FE0C3] font-medium self-end" data-id="element-280" data-mp="2qyen9frm0er1">
                        All-in
                      </span>
                      <span className="text-lg font-bold text-white text-right whitespace-nowrap leading-tight" data-id="element-281" data-mp="2vwzn3e2y8wey">
                        {phase.allInAmount}{' '}
                        <span className="text-slate-500 text-xs font-normal" data-id="element-282" data-mp="23bpyca46nm0x">
                          {phase.allInUnit}
                        </span>
                      </span>
                    </div>}
                </div>}
            </div>
          </div>)}
      </div>
    </div>;
}